import FJPoolStore from '../../../../store/FJPoolStore';
import FJUtil from '../../../../util/FJUtil';

// 原图画布
class FJOriginCanvas {
    _editImage = null; // 正在编辑中的图片
    _originImage = null; // 原图，用于实现对比功能

    // 将Url 和 _editImage 分开，防止跨域问题
    _editImageUrl = '';
    _originImageUrl = '';

    _editLoaded = false;
    _originLoaded = false;

    constructor() {}

    // 初始化原图画布
    async init(editImageUrl) {
        if (!editImageUrl || !editImageUrl === '') return;
        this._editImageUrl = editImageUrl;

        let imageUrl = editImageUrl;
        const isCrossOrigin = FJUtil.isCrossOrigin(editImageUrl);

        if (isCrossOrigin) {
            // 下载图片
            imageUrl = await FJUtil.getBlobByUrlSolveCrossOrigin(editImageUrl);
        }
        await new Promise((resolve, reject) => {
            // 判断editImageUrl 是否跨域

            this._editImage = new Image();
            this._editImage.src = imageUrl;

            this._editImage.onload = () => {
                this._editLoaded = true;
                resolve();
            };

            this._editImage.onerror = () => {
                reject();
            };
        });
    }

    /**
     * @desc 初始化原图
     * @param {string} imageUrl
     */
    async initOriginImage(originImageUrl) {
        this._originImageUrl = originImageUrl;

        const isCrossOrigin = FJUtil.isCrossOrigin(originImageUrl);
        let imageUrl = originImageUrl;

        if (isCrossOrigin) {
            // 下载图片
            imageUrl = await FJUtil.getBlobByUrlSolveCrossOrigin(originImageUrl);
        }

        this._originImage = new Image();
        this._originImage.src = imageUrl;

        await new Promise((resolve, reject) => {
            this._originImage.onload = () => {
                this._originLoaded = true;
                resolve();
            };

            this._originImage.onerror = () => {
                reject();
            };
        });
    }

    /**
     * @desc 绘制滑动对比模式
     * @param {CanvasRenderingContext2D} context
     * @param {Object} rect
     * @param {number} compareLineScale
     */
    _drawSlideCompare(context, rect, compareLineScale) {
        const originRect = {
            x: 0,
            y: 0,
            width: this._originImage.width * compareLineScale,
            height: this._originImage.height,
        };

        context.drawImage(
            this._originImage,
            originRect.x,
            originRect.y,
            originRect.width,
            originRect.height,
            rect.x,
            rect.y,
            rect.width * compareLineScale,
            rect.height,
        );

        const editRect = {
            x: this._editImage.width * compareLineScale,
            y: 0,
            width: this._editImage.width * (1 - compareLineScale),
            height: this._editImage.height,
        };

        context.drawImage(
            this._editImage,
            editRect.x,
            editRect.y,
            editRect.width,
            editRect.height,
            rect.x + rect.width * compareLineScale,
            rect.y,
            rect.width * (1 - compareLineScale),
            rect.height,
        );
    }

    /**
     * @desc 绘制原图
     * @param {CanvasRenderingContext2D} context
     * @param {Object} rect
     * @param {boolean} options.isCompare 是否是对比模式 显示原图
     * @param {boolean} options.slideCompare 是否是滑动对比模式 分割线左边显示原图 右边显示编辑图
     */
    draw(context, rect, options = {}) {
        if (!this._editLoaded) return;
        const { isCompare = false, slideCompare = false, compareLineScale = 0 } = options;

        if (slideCompare && this._originLoaded && this._editLoaded) {
            this._drawSlideCompare(context, rect, compareLineScale);
        } else if (isCompare && this._originLoaded) {
            context.drawImage(this._originImage, rect.x, rect.y, rect.width, rect.height);
        } else {
            context.drawImage(this._editImage, rect.x, rect.y, rect.width, rect.height);
        }
    }

    /**
     * @desc 获取编辑图片的尺寸
     * @returns
     */
    getSize() {
        if (!this._editLoaded || !this._editImage) return;
        return {
            width: this._editImage.width,
            height: this._editImage.height,
        };
    }

    /**
     * @desc 获取原图的尺寸
     * @returns
     */
    getOriginSize() {
        if (!this._originLoaded) return;
        return {
            width: this._originImage.width,
            height: this._originImage.height,
        };
    }

    /**
     * @desc 获取图片尺寸
     * @param {number} maxSize 最大尺寸
     * @returns
     */
    _getSize(maxSize) {
        let size = this.getSize();
        if (maxSize && (size.width > maxSize || size.height > maxSize)) {
            // 图片过大 压缩处理
            if (size.width > maxSize && size.height > maxSize) {
                if (size.width > size.height) {
                    size.height = Math.round((size.height / size.width) * maxSize);
                    size.width = maxSize;
                } else {
                    size.width = Math.round((size.width / size.height) * maxSize);
                    size.height = maxSize;
                }
            } else if (size.width > maxSize) {
                size.height = Math.round((size.height / size.width) * maxSize);
                size.width = maxSize;
            } else if (size.height > maxSize) {
                size.width = Math.round((size.width / size.height) * maxSize);
                size.height = maxSize;
            }
        }

        return size;
    }

    /**
     * @desc 获取编辑图片的blob
     * @param {number} maxSize 最大尺寸
     * @returns
     */
    async getImageBlob(maxSize) {
        if (!this._editLoaded) return;

        const size = this._getSize(maxSize);
        const canvas = FJPoolStore.getCanvas(undefined, true);
        canvas.width = size.width;
        canvas.height = size.height;
        const context = canvas.getContext('2d');

        context.drawImage(this._editImage, 0, 0, size.width, size.height);
        return await new Promise(resolve => {
            canvas
                .convertToBlob()
                .then(blob => {
                    resolve(blob);
                })
                .catch(() => {
                    resolve(null);
                });
        });
    }

    async getOriginImageBlob(maxSize) {
        if (!this._originLoaded) return;
        const size = this._getSize(maxSize);
        const canvas = FJPoolStore.getCanvas(undefined, true);
        canvas.width = size.width;
        canvas.height = size.height;
        const context = canvas.getContext('2d');

        context.drawImage(this._originImage, 0, 0, size.width, size.height);
        return await new Promise(resolve => {
            canvas
                .convertToBlob()
                .then(blob => {
                    resolve(blob);
                })
                .catch(() => {
                    resolve(null);
                });
        });
    }

    getImageUrl() {
        return this._editImageUrl;
    }

    getOriginImageUrl() {
        return this._originImageUrl;
    }

    getOriginImageLoaded() {
        return this._originLoaded;
    }

    getEditImageLoaded() {
        return this._editLoaded;
    }

    clear() {
        this._editImage = null;
        this._originImage = null;
        this._editImageUrl = '';
        this._originImageUrl = '';
        this._editLoaded = false;
        this._originLoaded = false;
    }
}

export default FJOriginCanvas;
